import { useEffect, useRef } from 'react';
import { BfIconClose } from './BfIcons';

/**
 * Универсальный premium bottom sheet.
 * - Drag handle, fade backdrop, snap к 92dvh.
 * - Tap по backdrop = onClose.
 * - Swipe вниз по handle = onClose (rubber-band).
 * - Блокирует скролл body на время жизни.
 * - Поддерживает reduce-motion и safe-area.
 */
export default function BfSheet({
  title,
  titleMark = false,
  onClose,
  showHeaderClose = true,
  rightActions = null,
  toolbar = null,
  children,
  footer = null,
  ariaLabel,
  headerClassName = '',
  bodyClassName = '',
  sheetClassName = '',
  backdropClassName = '',
}) {
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragDelta = useRef(0);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onHandlePointerDown = (e) => {
    dragStartY.current = e.clientY;
    dragDelta.current = 0;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* no-op */
    }
  };
  const onHandlePointerMove = (e) => {
    if (dragStartY.current == null) return;
    const dy = Math.max(0, e.clientY - dragStartY.current);
    dragDelta.current = dy;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${dy * 0.6}px)`;
    }
  };
  const onHandlePointerUp = () => {
    if (dragStartY.current == null) return;
    const dy = dragDelta.current;
    dragStartY.current = null;
    dragDelta.current = 0;
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
      sheetRef.current.style.transform = '';
      const tid = setTimeout(() => {
        if (sheetRef.current) sheetRef.current.style.transition = '';
      }, 320);
      void tid;
    }
    if (dy > 80) onClose?.();
  };

  return (
    <>
      <div
        className={['bf-sheet-backdrop', backdropClassName].filter(Boolean).join(' ')}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        className={['bf-sheet', sheetClassName].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bf-sheet__handle"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          aria-hidden="true"
        />
        {(title || rightActions || (onClose && showHeaderClose)) && (
          <div
            className={['bf-sheet__header', headerClassName].filter(Boolean).join(' ')}
          >
            <div className="bf-sheet__title">
              {titleMark && <span className="bf-sheet__title-mark" aria-hidden="true" />}
              {title ? <span>{title}</span> : null}
            </div>
            <div className="bf-sheet__actions">
              {rightActions}
              {onClose && showHeaderClose && (
                <button
                  type="button"
                  className="bf-icon-btn"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <BfIconClose />
                </button>
              )}
            </div>
          </div>
        )}
        {toolbar && <div className="bf-sheet__toolbar">{toolbar}</div>}
        <div className={['bf-sheet__body', bodyClassName].filter(Boolean).join(' ')}>{children}</div>
        {footer && <div className="bf-sheet__cta">{footer}</div>}
      </div>
    </>
  );
}
